import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  ALL_ORGANIZATIONS,
  ORGANIZATION_DETAIL_ADMIN,
  CREATE_ORGANIZATION,
  UPDATE_ORGANIZATION,
  SUSPEND_ORGANIZATION,
  REACTIVATE_ORGANIZATION,
  ADMIN_CREATE_ORGANIZATION,
  SEARCH_USERS,
} from "../graphql";
import type {
  AllOrganizationsResult,
  OrganizationDetailAdmin,
  OrganizationType,
  OrganizationTier,
  OrganizationStatus,
  AdminUser,
} from "../types";

// ─── Queries ─────────────────────────────────────────────────────────────────

interface UseAllOrganizationsOptions {
  search?: string;
  status?: OrganizationStatus;
  types?: OrganizationType[];
  tiers?: OrganizationTier[];
  sortField?: string;
  sortOrder?: string;
  limit?: number;
  offset?: number;
}

export function useAllOrganizations(options: UseAllOrganizationsOptions = {}) {
  const { search, status, types, tiers, sortField, sortOrder, limit = 20, offset = 0 } = options;
  return useQuery<{ allOrganizations: AllOrganizationsResult }>(
    ALL_ORGANIZATIONS,
    {
      variables: {
        input: {
          limit,
          offset,
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
          ...(types && types.length > 0 ? { types } : {}),
          ...(tiers && tiers.length > 0 ? { tiers } : {}),
          ...(sortField ? { sortField } : {}),
          ...(sortOrder ? { sortOrder } : {}),
        },
      },
      fetchPolicy: "cache-and-network",
    },
  );
}

export function useOrganizationDetailAdmin(organizationId: string) {
  return useQuery<{ organizationDetailAdmin: OrganizationDetailAdmin }>(
    ORGANIZATION_DETAIL_ADMIN,
    {
      variables: { organizationId },
      skip: !organizationId,
      fetchPolicy: "cache-and-network",
    },
  );
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useCreateOrganization() {
  return useMutation(CREATE_ORGANIZATION, {
    refetchQueries: ["AllOrganizations", "MyManagedOrganizations"],
  });
}

export function useUpdateOrganization() {
  return useMutation(UPDATE_ORGANIZATION, {
    refetchQueries: ["AllOrganizations", "MyManagedOrganizations"],
  });
}

export function useSuspendOrganization() {
  return useMutation(SUSPEND_ORGANIZATION, {
    refetchQueries: ["AllOrganizations", "MyManagedOrganizations"],
  });
}

export function useReactivateOrganization() {
  return useMutation(REACTIVATE_ORGANIZATION, {
    refetchQueries: ["AllOrganizations", "MyManagedOrganizations"],
  });
}

export function useAdminCreateOrganization() {
  return useMutation(ADMIN_CREATE_ORGANIZATION, {
    refetchQueries: ["AllOrganizations"],
  });
}

// ─── Lazy Queries ────────────────────────────────────────────────────────────

export function useSearchUsers() {
  return useLazyQuery<{
    searchUsers: AdminUser[];
  }>(SEARCH_USERS, {
    fetchPolicy: "network-only",
  });
}
